import { useState } from "react";
import { api } from "./lib/api";

type Tone = "good" | "ok" | "warn" | "info";
interface Block { id: string; title: string; tone: Tone; headline: string; lines: string[]; facts: Array<{ label: string; value: string }> }
interface Summary {
  days: number;
  level: { key: string; label: string; tone: Tone; text: string };
  blocks: Block[];
  steps: string[];
  notes: string[];
  connected: { searchConsole: boolean; googleAds: boolean };
  plainText: string;
  generatedAt: string;
}

const TONE_WORD: Record<Tone, string> = { good: "Bene", ok: "Discreto", warn: "Da migliorare", info: "Da completare" };

export default function SiteSummary({ siteId, onOpenSeo }: { siteId: string; onOpenSeo: () => void }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    setCopied(false);
    try {
      setSummary(await api<Summary>(`/api/sites/${siteId}/summary`));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Non sono riuscito a preparare il riassunto");
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary.plainText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setError("Non riesco a copiare: seleziona il testo e copialo a mano");
    }
  }

  if (!summary && !loading) {
    return <section className="sum-card">
      <div>
        <p className="eyebrow indigo">Non sei esperto?</p>
        <h3>Come sta andando il mio sito?</h3>
        <p>Con un clic leggi, in parole semplici, a che punto sei, come ti trova Google, quante persone ti contattano e cosa conviene fare adesso.</p>
        {error && <p className="sum-error" role="alert">{error}</p>}
      </div>
      <button className="sum-primary" onClick={load}>Riassumi il mio sito</button>
    </section>;
  }

  if (loading) return <section className="sum-card sum-loading" role="status" aria-live="polite"><div><h3>Sto leggendo i tuoi dati…</h3><p>Controllo le visite del sito, Google Search Console e gli annunci. Ci vogliono pochi secondi.</p></div><i /><i /><i /></section>;

  if (!summary) return null;
  return <section className="sum-result" aria-label="Riassunto del sito">
    <header className={`sum-level ${summary.level.tone}`}>
      <div>
        <small>Il tuo sito, ultimi {summary.days} giorni</small>
        <h3>{summary.level.label}</h3>
        <p>{summary.level.text}</p>
      </div>
      <div className="sum-actions">
        <button onClick={copy}>{copied ? "Copiato" : "Copia il riassunto"}</button>
        <button onClick={load}>Aggiorna</button>
        <button onClick={() => { setSummary(null); setError(""); }}>Chiudi</button>
      </div>
    </header>
    {error && <p className="sum-error" role="alert">{error}</p>}

    <div className="sum-grid">
      {summary.blocks.map((block) => <article key={block.id} className={`sum-block ${block.tone}`}>
        <header><span className="sum-dot" aria-hidden="true" /><h4>{block.title}</h4><em>{TONE_WORD[block.tone]}</em></header>
        <p className="sum-headline">{block.headline}</p>
        {block.lines.map((line) => <p key={line} className="sum-line">{line}</p>)}
        {block.facts.length > 0 && <dl>{block.facts.map((fact) => <div key={fact.label}><dd>{fact.value}</dd><dt>{fact.label}</dt></div>)}</dl>}
        {block.id === "visibility" && !summary.connected.searchConsole && <button className="sum-link" onClick={onOpenSeo}>Collega Search Console</button>}
      </article>)}
    </div>

    <section className="sum-steps">
      <h4>Cosa fare adesso</h4>
      <ol>{summary.steps.map((step) => <li key={step}>{step}</li>)}</ol>
    </section>

    <p className="sum-foot">
      {summary.notes.map((note) => <span key={note}>{note} </span>)}
      Calcolato ora con le statistiche del sito{summary.connected.searchConsole ? ", Google Search Console" : ""}{summary.connected.googleAds ? " e Google Ads" : ""}. Non viene salvato niente.
    </p>
  </section>;
}
